import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import cleaningMachine from "../../Assets/Images/cleaning Machine.png";
import washMachine from "../../Assets/Images/wash Machine.png";
import AutomotiveComponents from "../../Assets/Images/Automotive Components.png";
import comingSoonImg from "../../Assets/Images/coming soon.png";
import bg_img from "../../Assets/Images/OurProducts_bg.png";
import axiosInstance from "../../utils/axios";
import toast from "react-hot-toast";

const OurProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Static fallback products (in case API fails or for development)
  const staticProducts = [
    {
      img: cleaningMachine,
      title: "HHO Carbon Cleaning Machine",
    },
    {
      img: washMachine,
      title: "Steam Wash Machine",
    },
    {
      img: AutomotiveComponents,
      title: "Automotive Components",
    },
  ];

  const comingSoonProduct = {
    img: comingSoonImg,
    title: "Coming Soon",
    isComingSoon: true,
  };

  // Fetch all products from API
  const getAllProducts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/products/all");
      const apiProducts = response?.data || [];

      // Map API products with images from static products if available
      const mappedProducts = apiProducts.map((product) => {
        const matchedStatic = staticProducts.find(
          (staticProd) =>
            staticProd.title
              .toLowerCase()
              .includes(product.name.toLowerCase()) ||
            product.name.toLowerCase().includes(staticProd.title.toLowerCase())
        );

        return {
          ...product,
          img: product.images?.[0] || matchedStatic?.img || comingSoonImg,
          title: product.name,
        };
      });

      setProducts(mappedProducts);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setError(err.response?.data?.message || "Failed to load products");
      // Use static products as fallback
      setProducts(staticProducts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllProducts();
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error, { id: "products-error" });
    }
  }, [error]);

  // Handle product click
  const handleProductClick = (product) => {
    if (product.isComingSoon) {
      toast.info("This product is coming soon!", { id: "coming-soon" });
      return;
    }

    if (product._id) {
      navigate(`/our-products/${product._id}`);
    } else {
      toast.error("Product details not available", { id: "no-product-id" });
    }
  };

  // Filter out products that have both img and title
  const actualProducts = products.filter(
    (product) => product.img && product.title
  );

  // Ensure exactly 4 products are displayed
  let displayProducts = [...actualProducts];

  if (displayProducts.length < 4) {
    // Add "Coming Soon" placeholders until we have 4
    while (displayProducts.length < 4) {
      displayProducts.push(comingSoonProduct);
    }
  } else if (displayProducts.length > 4) {
    // Limit to only 4
    displayProducts = displayProducts.slice(0, 4);
  }

  // Loading skeleton
  const ProductSkeleton = () => (
    <div className="flex flex-col items-center bg-white justify-center border p-6 transition-all duration-300 mt-8 mb-8 rounded-md animate-pulse">
      <div className="w-full h-48 bg-gray-200 rounded mb-4"></div>
      <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
    </div>
  );

  return (
    <>
      <div className="py-10">
        {" "}
        <div
          style={{
            backgroundImage: `url(${bg_img})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="w-full bg-white py-10"
        >
          <div className="text-center">
            <p className="text-[#E8471B] font-montserrat font-medium text-[20px] leading-[24px] tracking-[0.5px] align-middle capitalize">
              Products
            </p>
          </div>
          <div className="text-center mt-4">
            <p className="font-orbitron font-semibold text-[45px] leading-[130%] tracking-[1px] align-middle capitalize text-[#FFFFFF]">
              Explore our Products
            </p>
          </div>
          <div className="px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-6">
            {loading
              ? [...Array(4)].map((_, index) => (
                  <ProductSkeleton key={`skeleton-${index}`} />
                ))
              : displayProducts.map((product, index) => (
                  <div
                    key={product._id || `product-${index}`}
                    onClick={
                      product.isComingSoon
                        ? undefined
                        : () => handleProductClick(product)
                    }
                    className={`flex flex-col items-center bg-white justify-center border p-6 transition-all duration-300 rounded-md ${
                      product.isComingSoon
                        ? "cursor-default opacity-80"
                        : "cursor-pointer"
                    }`}
                  >
                    <img
                      src={product.img}
                      alt={product.title || "Coming Soon"}
                      className="w-full h-auto object-contain mb-4"
                    />
                    {product.title && (
                      <h3 className="font-orbitron font-semibold text-[14.97px] leading-[17.96px] tracking-[0.37px] align-middle capitalize text-center">
                        {product.title}
                      </h3>
                    )}
                    {product.isComingSoon && (
                      <p className="text-gray-500 text-sm mt-2 italic">
                        Stay tuned for updates!
                      </p>
                    )}
                  </div>
                ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default OurProducts;
